/// <reference types="mapbox-gl" />

// packages
import { min, max } from 'd3-array';
import { geoPath } from 'd3-geo';
import { scaleThreshold } from 'd3-scale';
import { index } from 'd3-array';
import { ckmeans } from 'simple-statistics';

// components
import Legend from './Legend.svelte';
import HotspotsTooltip from './HotspotsTooltip.svelte';
import Controls from './Controls.svelte';
import Typeahead from '../../../utils/Typeahead/index.svelte';

// utils
import { last } from '../../../utils/last';

// config
import { MAPBOX_GL_ACCESS_TOKEN } from '../../../constants';

function createMap(container, bounds) {
  return new Promise((resolve) => {
    const map = new mapboxgl.Map({
      accessToken: MAPBOX_GL_ACCESS_TOKEN,
      container,
      style: `mapbox://styles/latimes/ckkktjs0f30ug17rxc9lw0ojx${
        process.env.NODE_ENV === 'development' ? '/draft' : ''
      }`,
      boxZoom: false,
      keyboard: false,
      dragRotate: false,
      bounds,
      fitBoundsOptions: { padding: 20 },
    });

    // we do this after init because we want our maxBounds to account for the fitBounds padding
    const fitBounds = map.getBounds();
    map.setMaxBounds(fitBounds);

    // this is gross, but so is the shape of LA County
    if (window.THIS_COUNTY === 'los_angeles') {
      map.fitBounds(
        [
          -118.94463111489618,
          33.7036765999509,
          -117.64637477037797,
          34.82330596,
        ],
        { padding: 20 }
      );
    }

    // Add zoom controls to the map
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }));

    // our trickery so we "know" when the map is ready to accept sources/layers
    map.on('load', () => {
      resolve(map);
    });
  });
}

const getBreaks = (data) => {
  // If there are more than 10 places use ckmeans breaks
  let breaks = [];
  if (data.length > 10) {
    breaks = ckmeans(
      data.map((d) => d.confirmed_cases_per_100k / 10),
      10
    )
      .map((d) => Math.round(last(d)))
      .slice(0, -1);
    // If there are fewer, we can use equal intervals
  } else {
    const values = data.map((d) => d.confirmed_cases_per_100k / 10);
    const minValue = min(values) * 0.9;
    const maxValue = max(values) * 1.1;
    const rangeValues = maxValue - minValue;
    const segments = 10;
    const breakLength = rangeValues / segments;
    for (let i = 0; i <= segments - 1; i++) {
      breaks.push(Math.round(minValue + (i + 1) * breakLength, 1));
    }
  }
  return breaks;
};

export async function boot(data, collection, bounds, includeTimeseries) {
  const container = document.querySelector('#mapbox-hotspots-map');
  const typeaheadContainer = document.querySelector('#mapbox-hotspots-lookup');
  const legendContainer = document.querySelector('#mapbox-hotspots-legend');
  const tooltipContainer = document.querySelector('#mapbox-hotspots-tooltip');
  const toggleContainer = document.querySelector('#mapbox-hotspot-controls');
  const breaks = getBreaks(data);

  const hotspotsColorScale = scaleThreshold()
    .domain([10, 20, 30, 40, 50, 100, 150, 200, 250])
    .range([
      '#e6dce8',
      '#d6c6d9',
      '#c6b1cb',
      '#b69cbd',
      '#a688ae',
      '#9773a0',
      '#876093',
      '#774c85',
      '#683977',
      '#58256a',
    ]);

  const totalsColorScale = scaleThreshold()
    .domain(breaks)
    .range([
      '#bee9f4',
      '#aad5e2',
      '#96c1d0',
      '#82aebf',
      '#6f9bad',
      '#5b889c',
      '#48768c',
      '#34657c',
      '#1f536c',
      '#00435c',
    ]);

  let legend;
  if (includeTimeseries == true) {
    legend = new Legend({
      target: legendContainer,
      props: {
        title: 'Cases per 10,000 residents over last 14 days',
        color: hotspotsColorScale,
        tickSize: 0,
      },
    });
  } else {
    legend = new Legend({
      target: legendContainer,
      props: {
        title: 'Cases per 10,000 residents',
        color: totalsColorScale,
        tickSize: 0,
      },
    });
  }

  const lookup = index(data, (d) => d.id);
  const featureLookup = index(collection.features, (d) => d.id);

  collection.features.forEach((feature) => {
    const properties = feature.properties;
    const datum = lookup.get(feature.id);

    // confirmed_cases_per_100k
    const hotspotsColor = datum
      ? hotspotsColorScale(
          Math.round(datum.new_confirmed_cases_fourteen_day_per_100k / 10)
        )
      : '#c6c6c6';
    const totalsColor = datum
      ? totalsColorScale(Math.round(datum.confirmed_cases_per_100k / 10))
      : '#c6c6c6';

    properties.hotspotsColor = hotspotsColor;
    properties.totalsColor = totalsColor;
  });

  const map = await createMap(container, bounds);

  // Find the index of the first symbol layer in the map style
  const layers = map.getStyle().layers;

  let firstSymbolId;

  for (var i = 0; i < layers.length; i++) {
    if (layers[i].type === 'symbol') {
      firstSymbolId = layers[i].id;
      break;
    }
  }

  map.addSource('places', {
    type: 'geojson',
    data: collection,
    promoteId: 'id',
  });

  map.addLayer(
    {
      id: 'places-fill-hover',
      source: 'places',
      type: 'fill',
      layout: {},
      paint: { 'fill-opacity': 0 },
    },
    firstSymbolId
  );

  if (includeTimeseries == true) {
    map.addLayer(
      {
        id: 'places-fill-hotspots',
        source: 'places',
        type: 'fill',
        layout: {
          visibility: 'visible',
        },
        paint: {
          'fill-color': ['get', 'hotspotsColor'],
        },
      },
      'waterways'
    );
  }

  map.addLayer(
    {
      id: 'places-fill-totals',
      source: 'places',
      type: 'fill',
      layout: {
        visibility: includeTimeseries ? 'none' : 'visible',
      },
      paint: {
        'fill-color': ['get', 'totalsColor'],
      },
    },
    'waterways'
  );

  map.addLayer({
    id: 'places-line-hover',
    source: 'places',
    type: 'line',
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
    },
    paint: {
      'line-color': '#000000',
      'line-width': 1.5,
      'line-opacity': [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        1,
        0,
      ],
    },
  });

  let hoveredStateId;
  let tooltipComponent;

  function setHoverStateId(id) {
    if (hoveredStateId) {
      map.setFeatureState(
        { source: 'places', id: hoveredStateId },
        { hover: false }
      );
    }

    map.setFeatureState({ source: 'places', id }, { hover: true });
    hoveredStateId = id;
  }

  function disableTooltip() {
    tooltipComponent.$destroy();
    tooltipComponent = undefined;
  }

  function removeTooltip() {
    if (hoveredStateId) {
      map.setFeatureState(
        { source: 'places', id: hoveredStateId },
        { hover: false }
      );

      hoveredStateId = undefined;
    }

    if (tooltipComponent) {
      disableTooltip();
    }
  }

  function activateTooltip(id, feature, x, y) {
    const data = lookup.get(id);

    let props;

    if (data) {
      const per_10k_hotspots =
        data.new_confirmed_cases_fourteen_day_per_100k != null
          ? Math.round(data.new_confirmed_cases_fourteen_day_per_100k / 10)
          : null;
      const per_10k =
        data.confirmed_cases_per_100k != null
          ? Math.round(data.confirmed_cases_per_100k / 10)
          : null;
      props = {
        title: data.name,
        total_cases: data.confirmed_cases,
        total_cases_hotspots: data.new_confirmed_cases_fourteen_day_total,
        per_10k_hotspots,
        per_10k,
        totals_color: totalsColorScale(per_10k),
        hotspots_color: hotspotsColorScale(per_10k_hotspots),
        no_data: false,
        includeTimeseries,
        x,
        y,
      };
    } else {
      const featureName = feature.properties.name;

      props = {
        title: featureName !== 'null' ? featureName : feature.id,
        no_data: true,
        x,
        y,
      };
    }

    if (tooltipComponent) {
      tooltipComponent.$set(props);
    } else {
      tooltipComponent = new HotspotsTooltip({
        target: tooltipContainer,
        props,
      });
    }
  }

  /** Saved reference to a geoPath instance for re-use. */
  const path = geoPath();

  new Typeahead({
    target: typeaheadContainer,
    props: {
      label: 'Find a place',
      onSelect: (d) => {
        const feature = featureLookup.get(d.id);
        if (feature) {
          const bounds = path.bounds(feature);
          setHoverStateId(d.id);

          map.fitBounds(bounds, { padding: 20 });
        }
      },
      onClear: () => {
        removeTooltip();
      },
      items: data,
      getItemValue: (d) => d.name,
    },
  });

  map.on('mousemove', 'places-fill-hover', (e) => {
    const features = e.features;

    if (features.length > 0) {
      const { x, y } = e.originalEvent;

      const feature = features[0];
      const id = feature.id;
      setHoverStateId(id);
      activateTooltip(id, feature, x, y);
    }
  });

  map.on('mouseleave', 'places-fill-hover', removeTooltip);

  function onDocumentTouch(e) {
    if (!container.contains(e.target)) removeTooltip();
  }

  document.addEventListener('touchstart', onDocumentTouch, false);

  // trick to make the tooltip move with the map on drag/panning
  map.on('drag', 'places-fill-hover', (e) => {
    if (tooltipComponent) {
      const { x, y } = e.originalEvent;
      tooltipComponent.$set({ x, y });
    }
  });

  let toggle;

  if (includeTimeseries == true) {
    toggle = new Controls({
      target: toggleContainer,
    });
  } else {
    toggle = new Controls({
      target: toggleContainer,
      props: {
        selectedValue: 'totals',
        buttonList: [{ label: 'Cumulative', value: 'totals' }],
      },
    });
  }

  toggle.$on('active', (event) => {
    const selected = event.detail;

    if (selected === 'hotspots') {
      legend.$set({
        title: 'Cases per 10,000 residents over last 14 days',
        color: hotspotsColorScale,
      });

      map.setLayoutProperty('places-fill-hotspots', 'visibility', 'visible');
      map.setLayoutProperty('places-fill-totals', 'visibility', 'none');
    }

    if (selected === 'totals') {
      legend.$set({
        title: 'Cases per 10,000 residents',
        color: totalsColorScale,
      });

      map.setLayoutProperty('places-fill-hotspots', 'visibility', 'none');
      map.setLayoutProperty('places-fill-totals', 'visibility', 'visible');
    }
  });
}
